<template>
  <section class="otoscopy-form pb-3">

    <div class="d-flex justify-content-between flex-wrap align-items-center">
      <h6 class="text-evollu-gray-light is-bold py-3 mb-0">
          {{$t('clinical.people.anamnesis.otoscopy')}}
      </h6>

      <div class="d-flex align-items-center justify-content-end">

        <div v-if="hasUnsavedChanges">
          <small class="text-orange is-bold">
            {{$t('clinical.people.anamnesis.unsaved')}}
          </small>
        </div>

        <div v-else-if="value && value.updated_at !== null && value.updated_at !== undefined">
          <small>
            {{$t('clinical.people.anamnesis.updated_at')}}
          </small>
          <small class="is-bold">
            {{$t('clinical.people.anamnesis.updated_by', {date: getExpressiveDate(value.updated_at), name: value.updated_by ? value.updated_by.name : ''})}}
          </small>
        </div>

        <i
          v-if="collapsable"
          class="clickable p-3"
          :class="{'fa fa-chevron-up': !isCollapsed, 'fa fa-chevron-down': isCollapsed}"
          @click="isCollapsed = !isCollapsed"
        >
        </i>
      </div>

    </div>

    <div v-if="!isCollapsed" class="mt-3">

      <slot name="beforeForm"></slot>

      <form class="evollu-form" v-if="value">

        <!-- Otoscopy Right Ear Result-->
        <div class="row align-items-center">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.right_ear')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6">
            <multiselect
              :value="formOptions.otoscopyEarResults.find(o => o.id === value.right_ear_otoscopy_result_id)"
              :options="formOptions.otoscopyEarResults"
              @input="v => updateValue('right_ear_otoscopy_result_id', v ? v.id : null)"
              track-by="id"
              :customLabel="option => $t(option.name)"
              select-label=""
              selected-label=""
              deselect-label=""
              :placeholder="$t('clinical.people.anamnesis.otoscopy_right_ear_result')"
              :allow-empty="true"
              :multiple="false"
              :searchable="true"
              open-direction="bottom"
              :disabled="!editing"
              >
              <span slot="noResult">{{$t('patients.noResults')}}</span>
            </multiselect>
          </div>

          <div class="text-danger text-break col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6 offset-sm-5 offset-md-4" v-if="formErrors.has('right_ear_otoscopy_result_id')">
            <small>
              {{$t(formErrors.get('right_ear_otoscopy_result_id'))}}
            </small>
          </div>
        </div>

        <!-- Otoscopy Right Ear Obs -->
        <div class="row align-items-start">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.right_ear_obs')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6">
            <va-input
              id="otoscopy-right-ear-obs"
              type="textarea"
              :min-rows="3"
              :placeholder="$t('clinical.people.anamnesis.right_ear_obs')"
              :value="value.right_ear_obs"
              @input="v => updateValue('right_ear_obs', v)"
              :error="formErrors.has('right_ear_obs')"
              :error-messages="[$t(formErrors.get('right_ear_obs'))]"
              :disabled="!editing"
            />
          </div>
        </div>

        <!-- Otoscopy Left Ear Result-->
        <div class="row align-items-center">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.left_ear')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6">
            <multiselect
              :value="formOptions.otoscopyEarResults.find(o => o.id === value.left_ear_otoscopy_result_id)"
              :options="formOptions.otoscopyEarResults"
              @input="v => updateValue('left_ear_otoscopy_result_id', v ? v.id : null)"
              track-by="id"
              :customLabel="option => $t(option.name)"
              select-label=""
              selected-label=""
              deselect-label=""
              :placeholder="$t('clinical.people.anamnesis.otoscopy_left_ear_result')"
              :allow-empty="true"
              :multiple="false"
              :searchable="true"
              open-direction="bottom"
              :disabled="!editing"
              >
              <span slot="noResult">{{$t('patients.noResults')}}</span>
            </multiselect>
          </div>

          <div class="text-danger text-break col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6 offset-sm-5 offset-md-4" v-if="formErrors.has('left_ear_otoscopy_result_id')">
            <small>
              {{$t(formErrors.get('left_ear_otoscopy_result_id'))}}
            </small>
          </div>
        </div>

        <!-- Otoscopy Left Ear Obs -->
        <div class="row align-items-start">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.left_ear_obs')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6">
            <va-input
              id="otoscopy-left-ear-obs"
              type="textarea"
              :min-rows="3"
              :placeholder="$t('clinical.people.anamnesis.left_ear_obs')"
              :value="value.left_ear_obs"
              @input="v => updateValue('left_ear_obs', v)"
              :error="formErrors.has('left_ear_obs')"
              :error-messages="[$t(formErrors.get('left_ear_obs'))]"
              :disabled="!editing"
            />
          </div>
        </div>

        <!-- Otoscopy Right Eardrum Result-->
        <div class="row align-items-center">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.right_eardrum')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6">
            <multiselect
              :value="formOptions.otoscopyEardrumResults.find(o => o.id === value.right_eardrum_otoscopy_result_id)"
              :options="formOptions.otoscopyEardrumResults"
              @input="v => updateValue('right_eardrum_otoscopy_result_id', v ? v.id : null)"
              track-by="id"
              :customLabel="option => $t(option.name)"
              select-label=""
              selected-label=""
              deselect-label=""
              :placeholder="$t('clinical.people.anamnesis.otoscopy_right_eardrum_result')"
              :allow-empty="true"
              :multiple="false"
              :searchable="true"
              open-direction="bottom"
              :disabled="!editing"
              >
              <span slot="noResult">{{$t('patients.noResults')}}</span>
            </multiselect>
          </div>

          <div class="text-danger text-break col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6 offset-sm-5 offset-md-4" v-if="formErrors.has('right_eardrum_otoscopy_result_id')">
            <small>
              {{$t(formErrors.get('right_eardrum_otoscopy_result_id'))}}
            </small>
          </div>
        </div>

        <!-- Otoscopy Right Eardrum Obs -->
        <div class="row align-items-start">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.right_eardrum_obs')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6">
            <va-input
              id="otoscopy-right-eardrum-obs"
              type="textarea"
              :min-rows="3"
              :placeholder="$t('clinical.people.anamnesis.right_eardrum_obs')"
              :value="value.right_eardrum_obs"
              @input="v => updateValue('right_eardrum_obs', v)"
              :error="formErrors.has('right_eardrum_obs')"
              :error-messages="[$t(formErrors.get('right_eardrum_obs'))]"
              :disabled="!editing"
            />
          </div>
        </div>

        <!-- Otoscopy Left Eardrum Result-->
        <div class="row align-items-center">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.left_eardrum')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6">
            <multiselect
              :value="formOptions.otoscopyEardrumResults.find(o => o.id === value.left_eardrum_otoscopy_result_id)"
              :options="formOptions.otoscopyEardrumResults"
              @input="v => updateValue('left_eardrum_otoscopy_result_id', v ? v.id : null)"
              track-by="id"
              :customLabel="option => $t(option.name)"
              select-label=""
              selected-label=""
              deselect-label=""
              :placeholder="$t('clinical.people.anamnesis.otoscopy_left_eardrum_result')"
              :allow-empty="true"
              :multiple="false"
              :searchable="true"
              open-direction="bottom"
              :disabled="!editing"
              >
              <span slot="noResult">{{$t('patients.noResults')}}</span>
            </multiselect>
          </div>

          <div class="text-danger text-break col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6 offset-sm-5 offset-md-4" v-if="formErrors.has('left_eardrum_otoscopy_result_id')">
            <small>
              {{$t(formErrors.get('left_eardrum_otoscopy_result_id'))}}
            </small>
          </div>
        </div>

        <!-- Otoscopy Left Eardrum Obs -->
        <div class="row align-items-start">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.left_eardrum_obs')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6">
            <va-input
              id="otoscopy-left-eardrum-obs"
              type="textarea"
              :min-rows="3"
              :placeholder="$t('clinical.people.anamnesis.left_eardrum_obs')"
              :value="value.left_eardrum_obs"
              @input="v => updateValue('left_eardrum_obs', v)"
              :error="formErrors.has('left_eardrum_obs')"
              :error-messages="[$t(formErrors.get('left_eardrum_obs'))]"
              :disabled="!editing"
            />
          </div>
        </div>

      </form>
      <div v-else class="ml-2">
        <slot name="noData">
          <span>{{$t('clinical.people.no_data')}}</span>
        </slot>
      </div>
    </div>

  </section>
</template>

<script>
import Errors from '@/utils/StandardErrors'
import Multiselect from 'vue-multiselect'
import * as AnamnesisOptions from './AnamnesisOptions'

export default {
  props: {
    value: {
      type: Object,
      required: false
    },
    formErrors: {
      type: Object,
      required: false,
      default: () => {
        return new Errors()
      }
    },
    editing: {
      type: Boolean,
      default: true
    },
    collapsable: {
      type: Boolean,
      default: false
    },
    collapsed: {
      type: Boolean,
      default: false
    }
  },
  components: {
    Multiselect
  },
  data: function () {
    return {
      isCollapsed: this.collapsable && this.collapsed,
      formOptions: AnamnesisOptions,
      hasUnsavedChanges: false
    }
  },
  methods: {
    getHasUnsavedChanges: function () {
      return this.hasUnsavedChanges
    },
    setHasUnsavedChanges: function (value) {
      this.hasUnsavedChanges = value
    },
    getDefaultValue: function () {
      return {
        left_ear_otoscopy_result_id: null,
        left_ear_obs: null,
        right_ear_otoscopy_result_id: null,
        right_ear_obs: null,
        left_eardrum_otoscopy_result_id: null,
        left_eardrum_obs: null,
        right_eardrum_otoscopy_result_id: null,
        right_eardrum_obs: null
      }
    },
    updateValue: function (field, value) {
      if (this.formErrors.has(field)) {
        this.formErrors.clear(field)
      }
      this.$emit('input', { ...this.value, [field]: value })
      this.$emit('clearErrors', field)

      if (!this.hasUnsavedChanges) {
        this.hasUnsavedChanges = true
      }
    },
    getExpressiveDate: function (value) {
      if (value) {
        if (this.$options.filters.isToday(value)) {
          return this.$t('date_helpers.today')
        } else {
          return this.$options.filters.localTime(value, `DD [${this.$t('date_helpers.day_of_month_separator')}] MMMM [${this.$t('date_helpers.day_of_month_separator')}] YYYY`, '', this.locale)
        }
      }
      return ''
    }
  },
  watch: {
    value: function (value) {
      if ((value === null || value === undefined) && this.editing) {
        this.$emit('input', { ...this.getDefaultValue() })
      }
    },
    collapsed: function () {
      this.isCollapsed = this.collapsable && this.collapsed
    },
    editing: {
      handler: function (editing) {
        if (editing) {
          this.$emit('input', { ...this.getDefaultValue(), ...this.value })
        }
      },
      immediate: true
    }
  }
}
</script>